export interface NumberFormatOptions extends Intl.NumberFormatOptions {
  minFD?: Intl.NumberFormatOptions['minimumFractionDigits']
  maxFD?: Intl.NumberFormatOptions['maximumFractionDigits']
}

export interface DateTimeFormatOptions extends Intl.DateTimeFormatOptions {
  delimiters?: string
  locale?: string
}

const DEFAULT_MIN_F_D = 2 // minimumFractionDigits
const DEFAULT_MAX_F_D = 2 // maximumFractionDigits
const DEFAULT_LOCALE = 'es-ES'
const DEFAULT_TIME_ZONE = 'Europe/Madrid'
const DEFAULT_NUMBER_STYLE: Intl.NumberFormatOptions['style'] = 'decimal'

const DATE_FORMAT_OPTIONS: DateTimeFormatOptions = {
  timeZone: DEFAULT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}
const TIME_FORMAT_OPTIONS: DateTimeFormatOptions = {
  timeZone: DEFAULT_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit'
}

type DateFormatter = { date: string; time: string; dateTime: string }
export type ValidationError = {
  data: {
    errors: Record<string, string[]>
  }
}

export function dateFormatter(
  value: string | Date | undefined | null,
  options?: DateTimeFormatOptions
): DateFormatter
export function dateFormatter(
  value: string | Date | undefined | null,
  options: DateTimeFormatOptions | undefined,
  result: 'date' | 'time' | 'dateTime'
): string
export function dateFormatter(
  value: string | Date | undefined | null,
  options: DateTimeFormatOptions = {},
  result?: 'date' | 'time' | 'dateTime'
): string | DateFormatter {
  const formatted = { date: '', time: '', dateTime: '' }
  if (!value) return result ? '' : formatted
  const parsedDate = typeof value === 'string' ? new Date(value) : value
  const dateOptions: DateTimeFormatOptions = { ...DATE_FORMAT_OPTIONS, ...options }
  const timeOptions: DateTimeFormatOptions = { ...TIME_FORMAT_OPTIONS, ...options }
  formatted.date = parsedDate.toLocaleDateString(options.locale || DEFAULT_LOCALE, dateOptions)
  formatted.time = parsedDate.toLocaleTimeString(options.locale || DEFAULT_LOCALE, timeOptions)
  formatted.dateTime = `${formatted.date}${options.delimiters || ' '}${formatted.time}`
  if (result === 'date') return formatted.date
  else if (result === 'time') return formatted.time
  else if (result === 'dateTime') return formatted.dateTime
  else return formatted
}

export function dateToLocaleFormat(date?: Date, locale = 'iso') {
  if (!date) return null
  if (locale === 'iso') return date.toISOString()
  return date.toLocaleDateString(locale !== 'iso' ? locale : 'fr-CA')
}

export function numberFormatter(
  number: number = 0,
  options: NumberFormatOptions = {}
): string {
  const formatOptions = {
    style: DEFAULT_NUMBER_STYLE,
    ...options,
    minimumFractionDigits:
      options.minFD ?? options.minimumFractionDigits ?? DEFAULT_MIN_F_D,
    maximumFractionDigits:
      options.maxFD ?? options.maximumFractionDigits ?? DEFAULT_MAX_F_D
  }
  return new Intl.NumberFormat(DEFAULT_LOCALE, formatOptions).format(number)
}

export function amountFormatter(value: string) {
  const [integerPart, decimalPart] = value.split(/[,.]/)
  let integerPartFormatted = integerPart.replace(/[^\d]/g, '')
  integerPartFormatted = new Intl.NumberFormat('es-ES').format(
    Number(integerPartFormatted)
  )
  return decimalPart
    ? `${integerPartFormatted}.${decimalPart.slice(0, 2)}`
    : integerPartFormatted
}
